import { useLayoutEffect, useRef } from "react";
import { isCancelledError, useMutation, type MutateOptions, type MutationFunctionContext } from "@tanstack/react-query";
import { useMCPQueryScope, type MCPQueryContext } from "./useMCPQueryScope";

type Resource = { scope: MCPQueryContext; cleanup: () => void; handoff: () => void };
type ScopedVariables<V> = { input: V; scope: MCPQueryContext; resource?: Resource };
type ScopedMutateOptions<D, E, V, C> = MutateOptions<D, E, V, C> & {
  // Only operation-owned resources, never active dialog/selection state. Runs
  // once on failure, scope disposal or unmount; never after acknowledged handoff.
  onCleanup?: () => void;
  // Synchronous observer delivery is distinct from transport success. Call
  // handoff only after authorization navigation succeeds. Otherwise we dispose.
  onResourceSuccess?: (data: D, handoff: () => void) => void;
};

export const canNotifyMCPMutation = (scope: MCPQueryContext, error?: unknown): boolean => {
  if (!scope.enabled || !scope.active || scope.controller.signal.aborted || isCancelledError(error)) return false;
  const cancellation = error as { name?: string; code?: string; __CANCEL__?: boolean } | undefined;
  return cancellation?.name !== "AbortError" && cancellation?.code !== "ERR_CANCELED" && !cancellation?.__CANCEL__;
};
type Options<D, V, C, E> = {
  mutationFn: (input: V, scope: MCPQueryContext) => Promise<D>;
  onMutate?: (input: V, scope: MCPQueryContext) => Promise<C> | C;
  onSuccess?: (data: D, input: V, scope: MCPQueryContext) => void;
  onError?: (error: E, input: V, context: C | undefined, scope: MCPQueryContext) => void;
};

// Capture the scope in VARIABLES, not in mutation options. TanStack observers
// update their options on rerender while an older mutation may still be pending.
export const useScopedMCPMutation = <D, V, C = undefined, E = Error>(options: Options<D, V, C, E>) => {
  const scope = useMCPQueryScope();
  const resources = useRef(new Set<Resource>());
  useLayoutEffect(() => {
    const owned = resources.current;
    return () => {
      for (const resource of owned) {
        if (resource.scope === scope) resource.cleanup();
      }
    };
  }, [scope]);
  const mutation = useMutation<D, E, ScopedVariables<V>, C>({
    mutationFn: ({ input, scope: origin }) => {
      if (!origin.enabled || !origin.active || origin.controller.signal.aborted) {
        return Promise.reject(new Error("MCP request scope is no longer active"));
      }
      return options.mutationFn(input, origin);
    },
    onMutate: ({ input, scope: origin }) => options.onMutate?.(input, origin) as Promise<C> | C,
    onSuccess: (data, { input, scope: origin }) => options.onSuccess?.(data, input, origin),
    onError: (error, { input, scope: origin }, context) => options.onError?.(error, input, context, origin),
    // Hook-level settlement survives observer unmount and newer mutate calls.
    // Cache settlement above is deliberately independent of resource/UI gates.
    onSettled: (_data, error, { scope: origin, resource }) => {
      if (resource) {
        if (error || !canNotifyMCPMutation(origin)) resource.cleanup();
        // Active transport success is not a resource handoff. Keep ownership
        // through the await before TanStack delivers the observer callback.
      }
    },
  });
  const callbacks = (perCall: ScopedMutateOptions<D, E, V, C> | undefined) => ({
    onSuccess: (data: D, { input, scope: origin, resource }: ScopedVariables<V>, context: C | undefined, mutationContext: MutationFunctionContext) => {
      try {
        if (canNotifyMCPMutation(origin)) {
          perCall?.onResourceSuccess?.(data, () => resource?.handoff());
          perCall?.onSuccess?.(data, input, context, mutationContext);
        }
      } finally {
        // Missing navigation, stale delivery, or a throwing handler must not
        // strand a window. This is a no-op after explicit handoff.
        resource?.cleanup();
      }
    },
    onError: (error: E, { input, scope: origin }: ScopedVariables<V>, context: C | undefined, mutationContext: MutationFunctionContext) => {
      if (canNotifyMCPMutation(origin, error)) perCall?.onError?.(error, input, context, mutationContext);
    },
    // Settlement cleanup (e.g. releasing an in-flight ref) is safe even after a transition.
    onSettled: (data: D | undefined, error: E | null, { input }: ScopedVariables<V>, context: C | undefined, mutationContext: MutationFunctionContext) =>
      perCall?.onSettled?.(data, error, input, context, mutationContext),
  });
  const variables = (input: V, perCall?: ScopedMutateOptions<D, E, V, C>): ScopedVariables<V> => {
    // One observer only delivers its latest per-call callbacks. A new mutate
    // supersedes older unhanded resources, not already navigated popups.
    for (const owned of resources.current) owned.cleanup();
    let resource: Resource | undefined;
    if (perCall?.onCleanup) {
      let owned = true;
      const release = () => {
        owned = false;
        resources.current.delete(resource!);
      };
      resource = {
        scope,
        cleanup: () => {
          if (!owned) return;
          release();
          perCall.onCleanup?.();
        },
        handoff: release,
      };
      resources.current.add(resource);
    }
    return { input, scope, resource };
  };
  return {
    ...mutation,
    variables: mutation.variables?.input,
    mutate: (input: V, perCall?: ScopedMutateOptions<D, E, V, C>) => mutation.mutate(variables(input, perCall), callbacks(perCall)),
    mutateAsync: (input: V, perCall?: ScopedMutateOptions<D, E, V, C>) => mutation.mutateAsync(variables(input, perCall), callbacks(perCall)),
  };
};
